import React, { useMemo } from 'react';
import styles from 'v2/common/DataVendorModals/modal.module.scss';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import restFactory from 'v2/utils/restFactory';
import { DataVendor } from 'v2/models';
import ConfirmationModal from 'v2/common/ConfirmationModal/ConfirmationModal';
import FormLabel from 'v2/form/FormLabel/FormLabel';
import classnames from 'classnames';
import RichTextEditor from 'v2/form/RichTextEditor/RichTextEditor';
import { TranslationKeys, PostProcessKeys } from 'translations';
import { useTranslation } from 'react-i18next';

interface EditDataVendorDescriptionModalProps {
  show: boolean;
  dataVendor: DataVendor;
  onClose: (dataVendor?: DataVendor) => void;
}

export interface DataVendorDescriptionField {
  description: string;
}

const EditDataVendorDescriptionModal = ({
  show,
  dataVendor,
  onClose,
}: EditDataVendorDescriptionModalProps) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    formState: { isValid },
    setValue,
  } = useForm<DataVendorDescriptionField>({
    mode: 'onChange',
    defaultValues: useMemo(() => {
      return { description: dataVendor?.description };
    }, [dataVendor?.description]),
  });

  const {
    isLoading: isDataVendorDescriptionMutateLoading,
    mutate: dataVendorDescriptionMutate,
  } = useMutation(
    async (data: DataVendorDescriptionField) => {
      return await restFactory.patch<DataVendor>(
        `/dj/api/v1/data_vendor/${dataVendor?.id}/`,
        data
      );
    },
    {
      onSuccess: (data: DataVendor) => {
        toast.success(
          `${t(TranslationKeys.dataProvider_one, {
            postProcess: PostProcessKeys.Capitalize,
          })} description saved.`
        );
        onClose(data);
      },
      onError: () => {
        toast.error(
          `Error saving ${t(TranslationKeys.dataProvider_one)} description.`
        );
      },
    }
  );

  const onSubmit = async (data: DataVendorDescriptionField) => {
    if (isValid) {
      dataVendorDescriptionMutate(data);
    }
  };

  return (
    <ConfirmationModal
      className={styles.viewModals}
      centered={true}
      animation={true}
      show={show}
      onCancel={() => onClose()}
      okBtnText="Save"
      onOk={handleSubmit(onSubmit)}
      okBtnDisabled={!isValid}
      isLoading={isDataVendorDescriptionMutateLoading}
      title="Edit Description"
    >
      <div>
        <div className={classnames(styles.dataVendorForm)}>
          <FormLabel
            label={`${t(TranslationKeys.provider_one, {
              postProcess: PostProcessKeys.TitleCase,
            })} Description`}
            required={false}
          />
          <div>
            <RichTextEditor
              readOnly={false}
              value={dataVendor?.description || ''}
              onChange={(data) =>
                setValue('description', data, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
            />
          </div>
        </div>
      </div>
    </ConfirmationModal>
  );
};

export default EditDataVendorDescriptionModal;
